import { useEffect, useState } from 'react';
import { MOCK_GUIDELINES, MOCK_JOBS } from '../mock/accuracyMockData';
import api from './api';
import type { GuidelineRef } from './accuracyGuidelineLabels';

export interface AccuracyJobMeta {
  id: string;
  job_name: string;
  guideline_id?: string;
  version?: number;
}

export interface AccuracyScopeMetadata {
  jobsById: Map<string, AccuracyJobMeta>;
  guidelineNamesById: Map<string, string>;
}

const EMPTY_METADATA: AccuracyScopeMetadata = {
  jobsById: new Map(),
  guidelineNamesById: new Map(),
};

let cachedMetadata: AccuracyScopeMetadata | null = null;
let loadPromise: Promise<AccuracyScopeMetadata> | null = null;

export async function loadAccuracyScopeMetadata(): Promise<AccuracyScopeMetadata> {
  if (cachedMetadata) return cachedMetadata;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const res: any = await api.jobs.getFilterOptions();
      const jobs: any[] = res?.data?.jobs || [];
      const guidelines: any[] = res?.data?.guidelines || [];

      const jobsById = new Map<string, AccuracyJobMeta>();
      for (const job of jobs) {
        if (!job?.id) continue;
        jobsById.set(job.id, {
          id: job.id,
          job_name: job.job_name || job.id,
          guideline_id: job.guideline_id || undefined,
          version: typeof job.version === 'number' ? job.version : Number(job.version || 0) || undefined,
        });
      }

      const guidelineNamesById = new Map<string, string>();
      for (const g of guidelines) {
        if (!g?.id) continue;
        guidelineNamesById.set(g.id, g.name || g.id);
      }

      cachedMetadata = { jobsById, guidelineNamesById };
      return cachedMetadata;
    } catch {
      cachedMetadata = EMPTY_METADATA;
      return cachedMetadata;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

export function useAccuracyScopeMetadata(): AccuracyScopeMetadata | null {
  const [metadata, setMetadata] = useState<AccuracyScopeMetadata | null>(cachedMetadata);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadAccuracyScopeMetadata();
      if (!cancelled) setMetadata(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return metadata;
}

export function getJobDisplayName(jobId: string, metadata?: AccuracyScopeMetadata | null): string {
  const fromApi = metadata?.jobsById.get(jobId)?.job_name;
  if (fromApi) return fromApi;
  const mock = MOCK_JOBS.find((j) => j.id === jobId);
  if (mock?.job_name) return mock.job_name;
  return jobId;
}

export function getGuidelineDisplayName(
  guidelineId: string,
  metadata?: AccuracyScopeMetadata | null
): string {
  if (!guidelineId) return '—';
  const fromApi = metadata?.guidelineNamesById.get(guidelineId);
  if (fromApi) return fromApi;
  const mock = MOCK_GUIDELINES.find((g) => g.id === guidelineId);
  if (mock?.name) return mock.name;
  return guidelineId;
}

export function formatGuidelineWithVersion(
  ref: GuidelineRef,
  metadata?: AccuracyScopeMetadata | null
): string {
  return `${getGuidelineDisplayName(ref.guideline_id, metadata)} v${ref.version}`;
}
